import React from 'react';
import PropTypes from 'prop-types';
import css from './CwComboBox.scss';
import CwDropdown from '../cwdropdown/CwDropdown';
import CwTriggerField from '../cwtriggerfield/CwTriggerField';
import { setMenuPositions, calcDropdownPosition } from '../../utils/CwDropdownService';
import { getMouseMode } from '../../utils/CwThemeService';
import { IsEmpty } from '../../utils/ValidationUtils';
import changeCheck from '../../utils/ChangeCheck';

/**
 * Based on react-autocomplete:
 * https://github.com/reactjs/react-autocomplete
 */

class CwComboBox extends React.Component {
  constructor(props) {
    super(props);

    this._typing = false;
    this._ignoreBlur = false;
    this._performAutoCompleteOnUpdate = false;
    this._performAutoCompleteOnKeyUp = false;
    this._triggerClicked = false;

    this.state = {
      displayText: this.props.nullOptionName,
      expanded: false,
      prevExpanded: true,
      highlightedIndex: null,
      filter: true
    };

    this.keyDownHandlers = {
      ArrowDown(event) {
        // Cycle through list of items on arrow down, updating `highlightedIndex` along the way
        event.preventDefault(); // Prevent window scrolling
        const itemsLength = this._triggerClicked ? this.getItems().length : this.getFilteredItems().length;
        if (!itemsLength) return;
        const { highlightedIndex } = this.state;
        const topIndex = this.props.pickerText ? -1 : 0;
        let index = (
          highlightedIndex === null ||
          highlightedIndex === itemsLength - 1
        ) ? topIndex : highlightedIndex + 1; // Go back to the top when reaching the end
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
          prevExpanded: false,
          filter: this._triggerClicked ? false : true
        });
      },

      ArrowUp(event) {
        // Cycle through list of items on arrow up, updating `highlightedIndex` along the way
        event.preventDefault(); // Prevent window scrolling
        const itemsLength = this._triggerClicked ? this.getItems().length : this.getFilteredItems().length;
        if (!itemsLength) return;
        const { highlightedIndex } = this.state;
        const topIndex = this.props.pickerText ? -1 : 0;
        let index = (
          highlightedIndex === topIndex ||
          highlightedIndex === null
        ) ? itemsLength - 1 : highlightedIndex - 1; // Go back to the end when reaching the top
        this._performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          expanded: true,
          prevExpanded: false,
          filter: this._triggerClicked ? false : true
        });
      },

      // Update value on shortcut keydown
      Alt(event) {
        this.selectItemFromKeyDown(event);
      },

      Control(event) {
        this.selectItemFromKeyDown(event);
      },

      Meta(event) {
        this.selectItemFromKeyDown(event);
      },
      Tab(event) {
        if (this.props.onPicker && this.state.highlightedIndex === null && this.isValueIncomplete()) {
          this.handleOnPicker();
        }
        else {
          this.selectItemFromKeyDown(event);
        }
      },
      Enter(event) {
        this._typing = false;

        if (this.props.onPicker && this.state.highlightedIndex === null) {
          this.handleOnPicker();
        }
        else {
          this.selectItemFromKeyDown(event);
        }
      },

      Escape() {
        this.setState({
          highlightedIndex: null,
          expanded: false,
          prevExpanded: true
        });
      },
    };
  }

  isValueIncomplete = () => {
    const valueText = this.props.value ? this.props.value.name : '';

    return valueText !== this.state.displayText;
  };

  handleOnPicker = () => {
    if (this.props.onPicker && !this.props.disabled) {
      const pickerText = this.isValueIncomplete() ? this.state.displayText : '';
      this.props.onPicker(pickerText);
    }
  };

  shouldComponentUpdate(nextProps, nextState) {
    const propsToCheck = ['value', 'disabled', 'invalid', 'items', 'readOnly'];
    const stateToCheck = ['displayText', 'expanded', 'highlightedIndex', 'filter'];
    return changeCheck(propsToCheck, this.props, nextProps) || changeCheck(stateToCheck, this.state, nextState) || (this.state.prevExpanded !== nextState.expanded);
  }

  componentWillMount() {
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentDidMount() {
    // Display value's name if already given one when combobox re-mounts (i.e. after pod collapses then re-expands)
    if (this.props.value && this.props.value.name) {
      this.setState({
        displayText: this.props.value.name
      });
    }
    else if (this.props.value !== '' && this.props.returnIdAsValue) {
      let items = this.props.items;
      items.forEach((item) => {
        if (item.id === this.props.value) {
          this.setState({
            displayText: item.name
          });
        }
      });
    }
    else if (this.props.value) {
      this.setState({
        displayText: this.props.value
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  componentWillReceiveProps(nextProps) {

    this._performAutoCompleteOnUpdate = true;
    // If `items` has changed we want to reset `highlightedIndex` since it probably no longer refers to a relevant item
    if (this.props.items !== nextProps.items ||
      // The entries in `items` may have been changed even though the object reference remains the same, double check by seeing if `highlightedIndex` points to an existing item
      this.state.highlightedIndex >= nextProps.items.length) {
      this.setState({ highlightedIndex: null });
    }
    // Display value's name if receiving it from the parent and user not currently typing in it
    if (nextProps.value && (nextProps.value.name !== undefined) && this._typing === false) {
      this.setState({
        displayText: nextProps.value.name
      });
    }
    else if (nextProps.value !== '' && this.props.returnIdAsValue) {
      let items = nextProps.items;
      items.forEach((item) => {
        if (item.id === nextProps.value) {
          this.setState({
            displayText: item.name
          });
        }
      });

    }
    else if (!nextProps.value && this._typing === false) {
      this.setState({
        displayText: this.props.nullOptionName
      });
    }
    if (this.props.onComponentWillReceiveProps) {
      this.props.onComponentWillReceiveProps(nextProps);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.expanded === true && prevState.expanded === false) {
      const menuPositions = setMenuPositions(this.cwTriggerField.cwTextField.inputField);
      this.setState({
        menuTop: menuPositions.menuTop,
        menuLeft: menuPositions.menuLeft,
        menuWidth: menuPositions.menuWidth,
        inputHeight: menuPositions.inputHeight
      });
    }

    if (this.state.expanded && this._performAutoCompleteOnUpdate) {
      this._performAutoCompleteOnUpdate = false;
      this.autoCompleteText();
    }

    if (prevState.expanded !== this.state.expanded) {
      if (this.state.expanded) {
        if (this.props.onExpand)
          this.props.onExpand(this.state.expanded);
      }
      else {
        if (this.props.onCollapse)
          this.props.onCollapse(this.state.expanded);
      }
    }
  }

  shouldItemRender(item, enteredText) {
    if (this.props.filterOnPartialMatch) {
      // Only render items that partially match the typed text
      return item.name.toLowerCase().indexOf(enteredText.toLowerCase()) > -1;
    }
    else {
      // Only render items that match up to what has been typed
      return item.name.toLowerCase().indexOf(enteredText.toLowerCase()) === 0;
    }
  }

  getItems() {
    let items = this.props.items.slice(0);

    if (this.props.hasNullOption === true) {
      items.unshift({
        id: null,
        name: this.props.nullOptionName,
        leftWidget: this.props.nullOptionLeftWidget
      });
    }

    return items;
  };

  getFilteredItems() {
    let items = this.getItems();
    if (this.shouldItemRender) {
      items = items.filter((item) => (
        this.shouldItemRender(item, this.state.displayText)
      ));
    }

    return items;
  }

  autoCompleteText() {
    if (this.state.displayText === '')
      return;
    let { highlightedIndex } = this.state;
    let items = this._triggerClicked ? this.getItems() : this.getFilteredItems();
    if (items.length === 0) { // No matches in dropdown
      this.setState({ highlightedIndex: null });
      return;
    }
    let matchedItem = highlightedIndex !== null ?
      items[highlightedIndex] : items[0];
    let itemValue = matchedItem ? matchedItem.name : '';
    let itemValueDoesMatch = this.props.filterOnPartialMatch ? (itemValue.toLowerCase().indexOf(this.state.displayText.toLowerCase() > -1)) :
      (itemValue.toLowerCase().indexOf(this.state.displayText.toLowerCase()) === 0);

    if (itemValueDoesMatch && highlightedIndex === null)
      this.setState({ highlightedIndex: 0 });
  }

  highlightItemFromMouse = (index) => {
    this.setState({ highlightedIndex: index });
  };

  selectItem = (item) => {
    if (item.id === -1) {
      // Picker selected
      this.setState({
        expanded: false,
        prevExpanded: true,
        highlightedIndex: null
      });

      this.handleOnPicker();
    }
    else if (item.id === null) {
      this.setState({
        displayText: this.props.nullOptionName,
        expanded: false,
        prevExpanded: true,
        highlightedIndex: null
      });
      if (this.props.onChange) {
        this.props.onChange(null);
      }
    }
    else {
      let displayText = item.name;
      let value = this.props.returnIdAsValue ? item.id : item;
      this.setState({
        displayText: displayText,
        expanded: false,
        prevExpanded: true,
        highlightedIndex: null
      });
      if (this.props.onChange) {
        // Report value of item selected if it changed
        if ((this.props.value === null) || (value.id !== this.props.value.id) || (this.props.returnIdAsValue && (value !== this.props.value))) {
          //remove the leftwidget from shape being passed up the chain so that Database does not complain. Should not affect boxes without leftwidget
          delete value.leftWidget;
          this.props.onChange(value);
        }
      }
    }
  };

  selectItemFromMouse = (item) => {
    this.selectItem(item);
    this.focus();
  };

  selectItemFromKeyDown = (event) => {
    if (this.state.expanded === false) {
      // Menu is closed so there is no selection to accept: do nothing
    }
    else if (this.state.highlightedIndex === -1) {
      // Picker selected
      this.setState({
        expanded: false,
        prevExpanded: true,
        highlightedIndex: null
      });

      this.handleOnPicker();
    }
    else if (this.state.highlightedIndex === null) { // Input has focus but no menu item is matched and Enter is hit
      if (this.props.allowCustomEntry) { // If custom entry allowed
        const displayText = this.state.displayText;
        const value = this.state.displayText;
        this.setState({
          displayText: displayText,
          expanded: false,
          prevExpanded: true,
          highlightedIndex: null
        });
        if (this.props.onChange) {
          this.props.onChange(value); // Report the custom value
        }
      }
      else { // If custom entry not allowed
        this.setState({
          displayText: this.props.nullOptionName, // Blank out display text
          expanded: false,
          prevExpanded: true
        });
        if (this.props.onChange) {
          this.props.onChange(null); // Report value of null
        }
      }
    }
    else { // Menu item is matched, so select it
      event.preventDefault();
      const item = this._triggerClicked ? this.getItems()[this.state.highlightedIndex] : this.getFilteredItems()[this.state.highlightedIndex];
      this.selectItem(item);
    }
  }

  setIgnoreBlur = (ignore) => {
    this._ignoreBlur = ignore;
  };

  handleScroll = (event) => {
    // Hide dropdown when window is scrolled
    if (this.state.expanded && (event.target === document || !event.target.className.includes('CwDropdown-dropDown'))) {
      this.setState({
        expanded: false,
        prevExpanded: true
      });
    }
  };

  handleKeyDown = (event) => {
    this._typing = true;
    if (this.keyDownHandlers[event.key]) {
      this.keyDownHandlers[event.key].call(this, event);
    }
    else {
      this._triggerClicked = false;
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  };

  handleKeyUp = () => {
    if (this._performAutoCompleteOnKeyUp) {
      this._performAutoCompleteOnKeyUp = false;
      this.autoCompleteText();
    }
  };

  handleChange = (enteredText) => {
    this._performAutoCompleteOnKeyUp = true;
    this.setState({
      displayText: enteredText,
      expanded: true,
      prevExpanded: false,
      filter: true,
    });
    if (enteredText === '') {
      this.setState({ highlightedIndex: null });
    }
    if (this.props.textChange) {
      this.props.textChange(enteredText);
    }
  };

  handleInputBlur = () => {
    this._typing = false;
    this._triggerClicked = false;
    if (this._ignoreBlur)
      return;

    // IE fires a blur event when clicking the scrollbar - this prevents the dropdown from closing
    if(this.cwDropdown && this.cwDropdown.isMousedOver()) {
      this.cwTriggerField.focus();
      return;
    }

    // Clear out invalid entries
    if (!this.props.pickerText && !this.props.allowCustomEntry) { // Don't clear out pickers as they can have values that aren't in the dropdown
      const filteredItems = this.props.items.filter((item) => {
        return item.name === this.state.displayText;
      });
      if (this.props.items.length > 0) { // Only check if items have been loaded
        if (filteredItems.length === 0) {
          this.setState({
            displayText: this.props.nullOptionName
          });

          // Check if we were previously empty, if we are we don't need to dirty the flag. 
          if (!IsEmpty(this.props.value)) {
            // We weren't empty so send null back to blank out he field. 
            if (this.props.onChange) {
              this.props.onChange(null); // Clear out if no matches in dropdown
            }
          }
        }
        else { // If valid, select the item
          this.selectItem(filteredItems[0]);
        }
      }
      else if (this.props.items.length === 0) { // If items don't exist yet and the value has changed, blank out the field

        const hasChanged = IsEmpty(this.props.value) ? !IsEmpty(this.state.displayText) : this.props.value.name !== this.state.displayText;

        if (hasChanged) {
          this.setState({
            displayText: this.props.nullOptionName
          });

          if (!IsEmpty(this.props.value)) {
            if (this.props.onChange) {
              this.props.onChange(null);
            }
          }
        }
      }
    }
    else if (this.state.displayText === '') { // Text blanked out, so null out value
      if (!IsEmpty(this.props.value)) {
        if (this.props.onChange) {
          this.props.onChange(null);
        }
      }
    }

    this.setState({
      highlightedIndex: null,
      expanded: false,
      prevExpanded: true
    });

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleInputFocus = () => {
    if (this.props.selectAllOnFocus) {
      this.cwTriggerField.cwTextField.inputField.select();
    }

    if (this._ignoreBlur) {
      this.setIgnoreBlur(false);
      return;
    }
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleTriggerClick = () => {
    // Expand the full unfiltered dropdown if the trigger is clicked
    if (this.props.disabled)
      return;
    this.focus();
    this.setState(
      {
        expanded: true,
        prevExpanded: false,
        filter: false
      }
    );
    this._triggerClicked = true;
  };

  focus() {
    this.cwTriggerField.cwTextField.focus();
  }

  render() {
    let items = this.getItems();
    const dropdownSettings = {
      top: this.state.menuTop,
      itemHeight: this.props.itemHeight
        ? this.props.itemHeight
        : getMouseMode() ? 24 : 32,
      inputHeight: this.state.inputHeight,
      maxItemsPerPage: 10,
      numOfItems: this.state.filter ? this.getFilteredItems().length : items.length,
      numOfExtraItems: this.props.pickerText ? 1 : 0
    };
    const dropdownPosition = calcDropdownPosition(dropdownSettings);

    let style = { // Pass calculated position coordinates to dropdown with inline style
      left: this.state.menuLeft,
      top: dropdownPosition.top,
      minWidth: this.state.menuWidth,
      height: dropdownPosition.menuHeight
    };

    return (
      <div
        data-cwid={this.props.cwId}
        className={css.comboBox}
      >
        <CwTriggerField
          cwId={this.props.cwId + '_trigger'}
          triggerIcon={this.props.triggerIcon}
          autoComplete="off"
          ref={(input) => this.cwTriggerField = input}
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          onKeyUp={this.handleKeyUp}
          value={this.state.displayText}
          disabled={this.props.disabled}
          invalid={this.props.invalid}
          readOnly={this.props.readOnly}
          onTrigger={this.handleTriggerClick}
          placeholder={this.props.placeholder}
          autoFocus={this.props.autoFocus}
          customElement={this.props.triggerCustomElement}
          className={this.props.triggerClassName}
        />
        {this.state.expanded
          ? <CwDropdown
            items={this.state.filter ? this.getFilteredItems() : items}
            itemHeight={this.props.itemHeight}
            highlightedIndex={this.state.highlightedIndex}
            style={style}
            onClick={this.selectItemFromMouse}
            onMouseEnter={this.highlightItemFromMouse}
            onMouseDown={this.setIgnoreBlur} // Ignore blur to prevent menu from de-rendering before we can process click
            filter={this.state.filter}
            pickerText={this.props.pickerText}
            ref={(dropdown) => this.cwDropdown = dropdown}
          />
          : null}
      </div>
    );
  }
}

CwComboBox.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Path to trigger icon
   */
  triggerIcon: PropTypes.string,
  /**
   * Array of items to be listed in the dropdown menu. Must contain at least "id" and "name" fields.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      name: PropTypes.string,
      leftWidget: PropTypes.element
    })
  ),
  /**
   * Represents the item that has been selected from the list (or string of custom item entered in if allowCustomEntry set to true). Returns an object {id,name} by default.
   */
  value: PropTypes.any,
  /**
   * Set to true if you want combobox to return just the id as the value instead of the object (i.e. for enums).
   */
  returnIdAsValue: PropTypes.bool,
  /**
   * Set to true if you want combobox to allow free text instead of only dropdown selection.
   */
  allowCustomEntry: PropTypes.bool,
  /**
   * Callback function that is fired when combobox's selection changes
   */
  onChange: PropTypes.func,
  /**
   * Callback function that is fired when combobox's dropdown menu is expanded
   */
  onExpand: PropTypes.func,
  /**
   * Callback function that is fired when is key is pressed
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback function that is fired when combobox's dropdown menu is collapsed
   */
  onCollapse: PropTypes.func,
  /**
   * Callback function that is fired when combobox's picker is clicked
   */
  onPicker: PropTypes.func,
  /**
   * Used to control if combobox is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable combobox
   */
  disabled: PropTypes.bool,
  /**
   * Text to display on combobox picker
   */
  pickerText: PropTypes.string,
  /**
   * Callback function that is fired when the component is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when the component is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Used to set the inner text field to a read only mode
   */
  readOnly: PropTypes.bool,
  /**
   * Callback function that is fired when text is changed
   */
  textChange: PropTypes.func,
  /**
   * Filters on partial matches instead of exact matches
   */
  filterOnPartialMatch: PropTypes.bool,
  /**
   * Hint text, displayed when user has not typed anything in
   */
  placeholder: PropTypes.string,
  /**
   * Set true to add an option to the dropdown for null
   */
  hasNullOption: PropTypes.bool,
  /**
   * Determines what text will display in the dropdown for the null option
   */
  nullOptionName: PropTypes.string,
  /**
   * Used to add a custom element to the left side of the null option
   */
  nullOptionLeftWidget: PropTypes.any,
  /**
   * Used to control if input field is autofocused when the page loads
   */
  autoFocus: PropTypes.bool,
  /**
   * Used to control if all text is highlighted on focus
   */
  selectAllOnFocus: PropTypes.bool,
  /**
   * Used to add custom css
   */
  className: PropTypes.string,
  /**
   * Used to add custom css to trigger field
   */
  triggerClassName: PropTypes.string,
  /**
   * Used to add a custom element to the left side of the trigger field
   */
  triggerCustomElement: PropTypes.any,
  /**
   * Used to override height on individual items in the drop down
   */
  itemHeight: PropTypes.number
};

CwComboBox.defaultProps = {
  returnIdAsValue: false,
  allowCustomEntry: false,
  value: '',
  hasNullOption: false,
  nullOptionName: '',
};

export default CwComboBox;


// WEBPACK FOOTER //
// ./src/components/cwcombobox/CwComboBox.jsx